import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Block } from 'app/interfaces/blocks';
import { Field } from 'app/interfaces/fields';
import { ContentBoxSettingsService } from 'app/main/pages/general-editor-tabs/content-box-settings/content-box-setting.service';
import { UserGalleryService } from 'app/main/pages/general-modals/user-gallery/user-gallery.service';
import { DomHelperService } from './dom-helper.service';
import { GlobalService } from './global.service';
import { SessionImageService } from './session-image.service';
import { IBrandingDto } from 'app/main/pages/settings/branding/branding.dto';
import { ImageBoxBlock } from 'app/interfaces/image';
import { ImageType } from 'app/main/pages/general-modals/user-gallery/user-galary.dto';

export interface Image {
  original: string;
}

export interface UploadParams {
  file_path : string
  uuid?: string|number
  location?: string
}

@Injectable({
  providedIn: 'root'
})

export class RefreshImageService {
  private renderer: Renderer2;
  constructor(
    private globalService: GlobalService,
    private userGalleryService: UserGalleryService,
    private rendererFactory: RendererFactory2,
    private contentBoxSettingsService: ContentBoxSettingsService,
    private domHelperService: DomHelperService,
    private sessionImageService: SessionImageService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  async collectTheImagesOfTemplate(section:string = 'document', id:number|string, branding: IBrandingDto, isOwner: boolean = true) {
    if (this.globalService.fields_data['blocks']) {
        const blocks = this.globalService.fields_data['blocks'];
        // Process blocks asynchronously
        const blockPromises = Object.keys(blocks).map(async (key) => {
            const block: Block = {...blocks[key]};
            if (block && block.type === 'image' && block?.file) {
                const uuid = block.uuid;
                const imageDom = document.querySelector(`[uuid="${uuid}"] img`);
                if(block.file.indexOf('brands') > -1 && branding){
                    if(block?.image_type === ImageType.BrandingLogo){
                        block.file = branding?.logo;
                    }else if(block?.image_type === ImageType.BrandingSecondaryLogo){
                        block.file = branding?.secondary_logo;
                    }
                }
                const image = this.sessionImageService.getTemplateImages(block?.file, Number(id), section);
                if (imageDom && block?.file) {
                    this.renderer.setAttribute(imageDom, 'src', image);
                    this.globalService.updateBlock(uuid, { file: block.file });
                }
            } else if (block?.type === 'attachment' && block?.files) {
                // Process attachment files
                await Promise.all(block.files.map(async (file) => {
                    const cloneFileObject = {...file};
                    try {
                        const attachmentDom = document.querySelector(`a[temp_uuid="${cloneFileObject.uuid}"]`) || document.querySelector(`a[uuid="${file.uuid}"]`);
                        const attachment: string = this.sessionImageService.getTemplateImages(file.url, Number(id), section);
                        if (attachmentDom) {
                            this.renderer.setAttribute(attachmentDom, 'href', attachment);
                            this.globalService.updateBlock(block.uuid, { url: file.url, files:[cloneFileObject] });
                        }
                    } catch (error) {
                        this.globalService.toastrError(error);
                    }
                }));
            } else if (block?.type === 'content-box' && block?.background_image) {
                const contentBoxImage = this.sessionImageService.getTemplateImages(block.background_image, Number(id), section);
                this.contentBoxSettingsService.setContentBoxImage(block.uuid, block.background_image, true, contentBoxImage);
            }else if(block?.type === 'upload-pdf' && block?.url){
                const pdfBlock = document.querySelector(`.pdf-block[uuid="${block.uuid}"]`);
                if(pdfBlock){
                  const refreshPdfUpload = this.sessionImageService.getTemplateImages(block.url, Number(id), section);
                  if(refreshPdfUpload){
                    this.renderer.setProperty(pdfBlock, 'style', `--pdf-background-image:url('${refreshPdfUpload}')`);
                  }
                }
            }else if(block && block.type !== 'image' && block?.file){
                const uuid = key;
                const imageDom = document.querySelector(`[uuid="${uuid}"] img`);
                if(block.file.indexOf('brands') > -1 && branding?.logo){
                  block.file = branding?.logo;
                }
                const image = this.sessionImageService.getTemplateImages(block.file, Number(id), section);
                if (imageDom) {
                    this.renderer.setAttribute(imageDom, 'src', image);
                    const imageBlock = ImageBoxBlock.block({ uuid: key, file: block.file });
                    this.globalService.updateBlock(key, imageBlock);
                }
            } else if(block?.type === 'product-table'){
                const table = document.querySelector(`.product-table-content[table-uuid="${block.uuid}"]`);
                if(table){
                    const productTableImages = table.querySelectorAll('.product-img-box');
                    productTableImages.forEach(imgTag => {
                        const productId = imgTag.getAttribute('product-id');
                        if(productId && block?.table){
                            const product = block?.table?.find(
                                p => Number(p.id) === Number(productId)
                            );
                            const imgPath = product?.images?.length
                                ? product.images[0].image_url
                                : '';
                            if(imgPath){
                                const imgUrl = this.sessionImageService.getTemplateImages(imgPath, Number(id), section);
                                this.renderer.setAttribute(imgTag, 'src', imgUrl);
                           }
                        }
                    });   
                }
            }
        });

        await Promise.all(blockPromises); // Wait for all block processing
    }
    for (const key in this.globalService.fields_data) {
      if (this.globalService.fields_data[key]) {
        const field: Field = this.globalService.fields_data[key];
        if (field.field == 'signature-field') {
          const signatureBox = document.querySelector(`.drag-fields[uuid="${field.uuid}"]`);
          if(!signatureBox) continue;
          if (field.selected_for_role_id == 0) {
            let signatureImage: string = this.sessionImageService.getSignatureImages();
            if(!isOwner){
              signatureImage = this.sessionImageService.getTemplateImages(field.value, Number(id), section);
              if(signatureBox.querySelector('.signature-image')){
                this.renderer.setStyle(signatureBox.querySelector('.signature-image'), 'background-image', 'url(' + signatureImage + ')');
              }
              continue;
            }
            this.renderer.setProperty(signatureBox.querySelector('.assignee'), 'textContent', this.globalService?.user_settings?.name);
            if (signatureBox?.querySelector('.signature-image') && this.globalService.user_settings.signature) {
              this.renderer.setStyle(signatureBox.querySelector('.signature-image'), 'background-image', 'url(' + signatureImage + ')');
              this.renderer.removeClass(signatureBox.querySelector('.signature-image'), 'hidden');
              this.renderer.addClass(signatureBox.querySelector('.field-label'), 'hidden');
              this.globalService.updateField(field.uuid, { value: this.globalService.user_settings.signature });
            }
            else {
              this.renderer.addClass(signatureBox.querySelector('.signature-image'), 'hidden');
              this.renderer.removeClass(signatureBox.querySelector('.field-label'), 'hidden');
              this.renderer.addClass(signatureBox.querySelector('.field-label'), 'generate-signature');
              this.globalService.updateField(field.uuid, { value: '' } , () => {
                this.domHelperService.initializeEventListener('.generate-signature', 'click', 'generateSignatureForSender');
              });
            }
          } else {
            if (signatureBox?.querySelector('.signature-image')) {
              if(field.value){
                const signatureImage: string = this.sessionImageService.getTemplateImages(field.value, Number(id), section);
                this.renderer.setStyle(signatureBox?.querySelector('.signature-image'), 'background-image', 'url(' + signatureImage + ')'); 
              }              
            }
          }
        }
      }
    }
  }

}